import { useEffect, useState } from "react";
import { Users, MousePointerClick, Eye, TrendingUp } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import StatCard from "@/components/admin/StatCard";
import PageHeader from "@/components/admin/PageHeader";

export default function AdminFootfall() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/footfall/stats"),
      api.get("/footfall/recent?limit=50")
    ])
      .then(([statsRes, recentRes]) => {
        setStats(statsRes.data);
        setRecent(recentRes.data);
      })
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader
          title="Footfall Analytics"
          subtitle="Track visitor activity across your platform"
        />

        {loading ? (
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-24 border border-[hsl(var(--zt-beige-deep))]" />
              ))}
            </div>
          </div>
        ) : stats ? (
          <div className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Visits" value={stats.total_visits} icon={Eye} color="teal" />
              <StatCard label="Unique Sessions" value={stats.unique_sessions} icon={MousePointerClick} color="gold" />
              <StatCard label="Unique Users" value={stats.unique_users} icon={Users} color="teal" />
              <StatCard label="Top Pages" value={stats.top_pages?.length || 0} icon={TrendingUp} color="gold" />
            </div>

            {stats.top_pages && stats.top_pages.length > 0 && (
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))]">
                  <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">
                    Top Pages
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-[hsl(var(--zt-beige-deep))]">
                        <th className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">Page</th>
                        <th className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_pages.map((item, i) => (
                        <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50">
                          <td className="px-4 py-3 text-sm text-[hsl(var(--zt-teal))]">{item.page}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-[hsl(var(--zt-charcoal))]">{item.visits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {recent.length > 0 && (
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))]">
                  <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">
                    Recent Visits
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-[hsl(var(--zt-beige-deep))]">
                        <th className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">Page</th>
                        <th className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">Device</th>
                        <th className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">Country</th>
                        <th className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((visit) => (
                        <tr key={visit.id} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/40 transition-colors">
                          <td className="px-4 py-3 text-sm text-[hsl(var(--zt-teal))]">{visit.page_path}</td>
                          <td className="px-4 py-3 text-xs text-[hsl(var(--zt-charcoal))]/70 capitalize">{visit.device_type || "—"}</td>
                          <td className="px-4 py-3 text-xs text-[hsl(var(--zt-charcoal))]/70">{visit.country || "—"}</td>
                          <td className="px-4 py-3 text-[11px] text-[hsl(var(--zt-charcoal))]/55">{fmtDate(visit.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </AdminLayout>
    </AdminGuard>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
}
